/**
 * Apothecary gallery carousel.
 *
 * Progressive enhancement over a native horizontally scrolling list: the cards
 * are keyboard-reachable on their own (they are links), so the prev/next arrows
 * are a pointer convenience that move the track by one card.
 * Arrow disabled state tracks the scroll position; reduced motion is respected
 * by the CSS `scroll-behavior`.
 */
class MgStoreGallery extends HTMLElement {
  /** @type {boolean} */
  #bound = false;

  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {HTMLButtonElement | null} */
  #prev = null;

  /** @type {HTMLButtonElement | null} */
  #next = null;

  /** @type {number | undefined} */
  #scrollRaf;

  connectedCallback() {
    if (this.#bound) return;
    this.#bound = true;

    this.#track = this.querySelector('[data-mg-track]');
    this.#prev = this.querySelector('[data-mg-prev]');
    this.#next = this.querySelector('[data-mg-next]');

    if (!this.#track) return;

    this.#prev?.addEventListener('click', () => this.#page(-1));
    this.#next?.addEventListener('click', () => this.#page(1));
    this.#track.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onScroll);

    // Arrows stay out of the tab order (aria-hidden pointer convenience): the
    // cards themselves are focusable and scroll into view on focus, so keyboard
    // users never need the arrows.
    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#track?.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onScroll);
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
  }

  /** @param {number} direction -1 (prev) or 1 (next) */
  #page(direction) {
    if (!this.#track) return;
    const amount = this.#pageAmount();
    this.#track.scrollBy({ left: amount * direction });
  }

  /** @returns {number} one slide step (card width + gap), falling back to the viewport width. */
  #pageAmount() {
    if (!this.#track) return 0;
    const slide = this.#track.querySelector('.mg-store-gallery__slide');
    if (slide instanceof HTMLElement) {
      const gap = parseFloat(getComputedStyle(this.#track).columnGap || '0') || 0;
      return slide.offsetWidth + gap;
    }
    return this.#track.clientWidth;
  }

  #onScroll = () => {
    if (this.#scrollRaf) cancelAnimationFrame(this.#scrollRaf);
    this.#scrollRaf = requestAnimationFrame(() => this.#updateArrows());
  };

  #updateArrows() {
    if (!this.#track || !this.#prev || !this.#next) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.#track;
    const maxScroll = scrollWidth - clientWidth;
    const atStart = scrollLeft <= 1;
    const atEnd = scrollLeft >= maxScroll - 1;
    this.#prev.toggleAttribute('disabled', atStart);
    this.#next.toggleAttribute('disabled', atEnd);
  }
}

if (!customElements.get('mg-store-gallery')) {
  customElements.define('mg-store-gallery', MgStoreGallery);
}
